'use client';

import Introduce from '@public/svgs/introduce.svg';
import Map from '@public/svgs/map.svg';
import Organization from '@public/svgs/organization.svg';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

/**
 * 메인 배너 대체 컴포넌트 (이미지 + 우측 퀵링크)
 *
 * 왼쪽에는 메인 슬라이드 이미지와 텍스트,
 * 오른쪽에는 퀵링크 카드들을 배치한 레이아웃
 */
export function MainBannerAlt() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const slides = [
    {
      image: '/banner/banner_01.jpg',
      title: '언어를 배우다, 시대와 통하다',
      subtitle: '언제 어디서나, 글로벌 교육으로 나의 가능성을 펼쳐보세요',
    },
    {
      image: '/banner/banner_02.jpg',
      title: '글로벌 인재 양성',
      subtitle: '세계와 소통하는 미래형 인재로 성장하세요',
    },
    {
      image: '/banner/banner_03.png',
      title: '지역과 함께하는 교육',
      subtitle: '지역사회와 함께 발전하는 교육 플랫폼',
    },
  ];

  const quickLinks = [
    {
      title: '인사말',
      description: '사업단장의 인사말을 확인하세요',
      link: '/greeting',
      Icon: Introduce,
      bgColor: 'bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#334155]',
      textColor: 'text-white',
    },
    {
      title: '조직도',
      description: 'RISE사업단의 조직 구성을 확인하세요',
      link: '/organization',
      Icon: Organization,
      bgColor: 'bg-gradient-to-br from-[#1e3a8a] via-[#3b82f6] to-[#60a5fa]',
      textColor: 'text-white',
    },
    {
      title: '오시는 길',
      description: '충북대학교 RISE사업단 찾아오시는 방법을 안내합니다',
      link: '/location',
      Icon: Map,
      bgColor: 'bg-gradient-to-br from-[#f59e0b] via-[#fbbf24] to-[#fcd34d]',
      textColor: 'text-gray-900',
    },
  ];

  // 애니메이션 초기화 및 슬라이드 자동 전환
  useEffect(() => {
    setIsMounted(true);

    const animationTimer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    // 5초마다 슬라이드 전환
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => {
      clearTimeout(animationTimer);
      clearInterval(slideInterval);
    };
  }, [slides.length]);

  const shouldShowAnimation = isMounted && isVisible;

  return (
    <section className="relative w-full bg-gray-50 pt-24 md:pt-28 lg:pt-32 pb-6 px-4 lg:px-6">
      <div className="max-w-[1920px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* 왼쪽: 메인 배너 슬라이더 */}
          <div className="relative w-full lg:w-[calc(100%-440px)] h-[520px] md:h-[700px] overflow-hidden bg-black rounded-3xl shadow-2xl">
            {/* 배경 이미지 슬라이더 */}
            <div className="absolute inset-0 w-full h-full">
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <Image
                    src={slide.image}
                    alt={`RISE 사업단 배너 ${index + 1}`}
                    fill
                    className="object-cover"
                    priority={index === 0}
                    quality={90}
                  />
                </div>
              ))}

              {/* 딤드 오버레이 */}
              <div className="absolute inset-0 bg-black/40" />
            </div>

            {/* 메인 콘텐츠 영역 */}
            <div className="relative z-20 w-full h-full px-8 md:px-16">
              <div className="max-w-[1200px] h-full flex flex-col justify-center">
                {/* 메인 텍스트 영역 */}
                <div
                  className={`space-y-6 ${
                    shouldShowAnimation
                      ? 'animate-fadeInUp'
                      : 'opacity-0 translate-y-8'
                  }`}
                >
                  <h1 className="text-[40px] md:text-[56px] lg:text-[64px] font-bold text-white leading-tight drop-shadow-2xl">
                    {slides[currentSlide].title}
                  </h1>
                  <p className="text-lg md:text-xl lg:text-2xl font-medium text-gray-100 drop-shadow-lg">
                    {slides[currentSlide].subtitle}
                  </p>

                  {/* CTA 버튼 */}
                  <button className="mt-8 px-8 py-4 bg-white text-blue-600 rounded-full font-semibold text-lg hover:bg-blue-50 transition-colors duration-300 shadow-lg">
                    입학 지원센터 바로가기
                  </button>
                </div>
              </div>
            </div>

            {/* 슬라이드 인디케이터 */}
            <div className="absolute bottom-8 left-8 md:left-16 flex gap-2 z-30">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'bg-white w-12'
                      : 'bg-white/50 w-8 hover:bg-white/75'
                  }`}
                  aria-label={`슬라이드 ${index + 1}로 이동`}
                />
              ))}
            </div>
          </div>

          {/* 오른쪽: 퀵링크 카드 섹션 */}
          <div className="w-full lg:w-[440px] space-y-4">
            {quickLinks.map((link, index) => {
              const IconComponent = link.Icon;
              return (
                <Link
                  key={index}
                  href={link.link}
                  className={`block ${link.bgColor} ${link.textColor} rounded-2xl p-8 hover:scale-[1.02] hover:shadow-2xl transition-all duration-300 shadow-lg relative overflow-hidden group`}
                >
                  {/* 배경 장식 */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-500" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12 group-hover:scale-150 transition-transform duration-500" />

                  {/* 아이콘 */}
                  <div className="relative w-16 h-16 mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <div className="absolute inset-0 bg-white/20 rounded-2xl blur-xl group-hover:bg-white/30 transition-all" />
                    <div className="relative w-full h-full flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                      <IconComponent className="w-8 h-8" />
                    </div>
                  </div>

                  {/* 카드 내용 */}
                  <div className="relative space-y-3">
                    <h3 className="text-2xl font-bold tracking-tight">
                      {link.title}
                    </h3>
                    <p className="text-sm opacity-90 leading-relaxed">
                      {link.description}
                    </p>
                  </div>

                  {/* 화살표 아이콘 */}
                  <div className="absolute bottom-8 right-8 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
